import { Box, Snackbar, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import strings from "global/constants/StringConstants";

interface CustomProps {
  showNotification: boolean;
  verticalPosition: "top" | "bottom";
  horizontalPosition: "left" | "right";
  variant: string;
  title: string;
  message: string;
  handleClose: any;
}

type NotificationVariantKey = "success" | "info" | "warning" | "error";

// Map the emitted variant string onto one of the four tinted-fill token sets.
const resolveVariant = (variant: string): NotificationVariantKey => {
  switch (variant) {
    case strings.success:
      return "success";
    case strings.warning:
      return "warning";
    case strings.error:
      return "error";
    default:
      return "info";
  }
};

const getNotificationTitle = (variant: NotificationVariantKey) => {
  switch (variant) {
    case "success":
      return "Success!";
    case "warning":
      return "Oops!";
    case "error":
      return "Oops!";
    default:
      return "Info";
  }
};

// Line icon per status, tinted with the status color (design 3A). Colors are
// passed in from theme tokens — never hardcoded here.
const StatusIcon = ({
  variant,
  color,
}: {
  variant: NotificationVariantKey;
  color: string;
}) => {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: color,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    width: 18,
    height: 18,
    style: { flexShrink: 0, marginTop: 1 },
    "aria-hidden": true,
  };
  switch (variant) {
    case "success":
      return (
        <svg {...common} strokeWidth={2.4}>
          <path d="M20 6L9 17l-5-5" />
        </svg>
      );
    case "warning":
      return (
        <svg {...common} strokeWidth={2}>
          <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
          <path d="M12 9v4" />
          <path d="M12 17h.01" />
        </svg>
      );
    case "error":
      return (
        <svg {...common} strokeWidth={2.2}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v4" />
          <path d="M12 16h.01" />
        </svg>
      );
    default:
      return (
        <svg {...common} strokeWidth={2.2}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 16v-4" />
          <path d="M12 8h.01" />
        </svg>
      );
  }
};

const CloseIcon = ({ color }: { color: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={2.2}
    strokeLinecap="round"
    width={14}
    height={14}
    aria-hidden="true"
  >
    <path d="M18 6 6 18" />
    <path d="M6 6l12 12" />
  </svg>
);

const CustomNotification = (props: CustomProps) => {
  const theme = useTheme();
  const variantKey = resolveVariant(props.variant);
  const tokens = theme.palette.notification[variantKey];
  const heading = getNotificationTitle(variantKey);

  return (
    <Snackbar
      open={props.showNotification}
      anchorOrigin={{
        vertical: props.verticalPosition,
        horizontal: props.horizontalPosition,
      }}
      autoHideDuration={strings.autoHideDuration}
      onClose={props.handleClose}
      sx={{ zIndex: 9999999, width: "auto" }}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <Box
        role="region"
        aria-label={`${heading} notification`}
        sx={{
          width: "360px",
          maxWidth: "calc(100vw - 32px)",
          backgroundColor: tokens.bg,
          border: "1px solid",
          borderColor: tokens.border,
          borderRadius: "12px",
          boxShadow: theme.palette.notification.shadow,
          padding: "14px 16px",
          display: "flex",
          gap: "12px",
          alignItems: "flex-start",
        }}
      >
        <StatusIcon variant={variantKey} color={tokens.icon} />

        <Stack spacing="2px" sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 800,
              lineHeight: 1.4,
              color: tokens.title,
            }}
          >
            {heading}
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              lineHeight: 1.5,
              color: tokens.text,
              wordBreak: "break-word",
            }}
          >
            {props.message}
          </Typography>
        </Stack>

        <Box
          component="button"
          type="button"
          onClick={props.handleClose}
          aria-label="Close notification"
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            flexShrink: 0,
            marginTop: "2px",
            p: 0,
            border: "none",
            background: "transparent",
            "&:focus-visible": {
              outline: `2px solid ${tokens.icon}`,
              outlineOffset: "2px",
            },
          }}
        >
          <CloseIcon color={tokens.icon} />
        </Box>
      </Box>
    </Snackbar>
  );
};

export default CustomNotification;
